/**
 * FW-ONB-01 (SS001-19) — turning the Setup checkbox column into the two
 * override lists the API takes.
 *
 * The server owns the defaults: it decides which never-track groups are
 * excluded, including the site-share guard that spares a folder big enough to
 * be the site itself. The client therefore sends **exceptions**, not a full
 * selection, and needs two lists rather than one — `keep` un-excludes a
 * suggestion, `exclude` drops a guard-spared group. With only `keep`, a
 * guard-spared folder could never be excluded at all; with only `exclude`, an
 * empty list would be indistinguishable from "the operator unticked
 * everything".
 *
 * The client therefore stores **only the boxes the operator touched**, and
 * every untouched box reads straight off the current `defaultExcluded`. Caching
 * the resolved tick state instead would go stale the moment a re-estimate moves
 * a group across the guard threshold — a `/help` tree that was 45% of the site
 * before the locale filter and 9% after would stay ticked as though the
 * operator had asked for it.
 *
 * Mirrors `backend/src/utils/never-track-paths.ts` — change one, check the other.
 */

export interface NeverTrackSelectionGroup {
  key: string;
  /** The server's verdict for this group, recomputed on every estimate. */
  defaultExcluded: boolean;
}

/** Group key → the operator's explicit "track this" choice. Untouched groups are absent. */
export type NeverTrackOverrideMap = ReadonlyMap<string, boolean>;

export interface NeverTrackOverrides {
  keepNeverTrackKeys: string[];
  excludeNeverTrackKeys: string[];
}

/** Whether a group's checkbox is ticked: the operator's call if they made one, else the server's. */
export function isNeverTrackGroupTracked(
  group: NeverTrackSelectionGroup,
  overrides: NeverTrackOverrideMap
): boolean {
  const explicit = overrides.get(group.key);
  return explicit === undefined ? !group.defaultExcluded : explicit;
}

export function neverTrackOverrides(
  groups: readonly NeverTrackSelectionGroup[],
  overrides: NeverTrackOverrideMap
): NeverTrackOverrides {
  const keepNeverTrackKeys: string[] = [];
  const excludeNeverTrackKeys: string[] = [];
  for (const group of groups) {
    const tracked = isNeverTrackGroupTracked(group, overrides);
    if (group.defaultExcluded && tracked) keepNeverTrackKeys.push(group.key);
    if (!group.defaultExcluded && !tracked) excludeNeverTrackKeys.push(group.key);
  }
  return { keepNeverTrackKeys, excludeNeverTrackKeys };
}

/**
 * Carry the operator's choices across a re-estimate, dropping any for a group
 * the new estimate no longer offers so a stale key can't be sent back.
 */
export function pruneNeverTrackOverrides(
  groups: readonly NeverTrackSelectionGroup[],
  overrides: NeverTrackOverrideMap
): Map<string, boolean> {
  const valid = new Set(groups.map((g) => g.key));
  return new Map(Array.from(overrides).filter(([key]) => valid.has(key)));
}

/** Tick or untick every currently offered group ("Track all" / "Exclude all"). */
export function setAllNeverTrackOverrides(
  groups: readonly NeverTrackSelectionGroup[],
  tracked: boolean
): Map<string, boolean> {
  return new Map(groups.map((g) => [g.key, tracked]));
}
